// janus-engine/inventory/payload-chain-frame.ts — R1b-2f (Janus round 4, session 4; owner 2026-09-29: "the front end is the system, the
// backend is a lazy save"): a destruction-payload CHAIN as ONE frame.
//
// PURE. The GM's page has already decided the whole chain (payload-chain.ts, pre-draw); this module turns that decision into the ordered
// write list legacy produces (the root destruction; per job, per step, its rows — cut at every destruction the step caused, which lands
// right after its target's damage row) and applies it to an encounter the page holds: the roster swaps (destroy-object.ts frames), the
// payload's statuses (a zone anchor on the encounter, a status on a creature or object), object vitality and creature vitality. The same
// frame is relayed to every other page at once and saved lazily by options/game/actions/destruction_chain_save_inc.php in this order.
import { applyObjectDestruction, type ObjectDestroyCommand } from './destroy-object.js';
import type { ChainJob, StepSegment } from './payload-chain.js';
import { inventoryField } from '../../shared/array-types.js';
import type { OptimisticDraw } from '../../shared/revert-on-refusal.js';
import type { UndoReport } from '../../shared/undo-frame.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

export const DESTRUCTION_CHAIN_SCHEMA = 'janus.destruction-chain.v1' as const;

/** One step's verdict slice as the chain frame carries it (Lane A's PayloadVerdict fields the save and the pages read). */
export interface ChainVerdict {
  rows: Row[];
  statusesAdded: Row[];
  objectDamage: Array<{ inventory_id: string; v_current: number; v_temp: number; destroyed: boolean }>;
  ec: Record<string, Record<string, unknown>>;
}
export type ChainOp =
  | { op: 'destroy'; command: ObjectDestroyCommand }
  /** One segment of one payload step: its log rows, and — in the segment where they land — its statuses and vitality writes. */
  | { op: 'step'; source_inventory_id: string; ability_id: string; caster: { kind: 'user' | 'creature'; id: string }; roll_id: string;
      rows: Row[]; statuses: Row[]; objects: ChainVerdict['objectDamage']; creatures: Record<string, Record<string, unknown>> };

export interface DestructionChainFrame {
  schema: typeof DESTRUCTION_CHAIN_SCHEMA;
  operation_id: string;
  campaign_id: string;
  encounter_id: string;
  ops: ChainOp[];
  removal?: import('../statuses/gm-boundary').GmBoundaryVerdict;
}

/**
 * Legacy's write order: the root destruction, then per step every segment's rows followed by the destructions it caused. A status lands in
 * the step's LAST segment (its table has no order; its log row does), an object / creature write in the segment of its damage row. Each
 * status row gets a page-minted `status_id` (the save persists it under that id, so every page holds the same row).
 */
export function buildChainOps(root: ObjectDestroyCommand, chain: { jobs: Array<Pick<ChainJob, 'job' | 'steps'> & { verdicts: ChainVerdict[] }> },
  segments: StepSegment[], uuid: () => string): ChainOp[] {
  const ops: ChainOp[] = [{ op: 'destroy', command: root }];
  const damageRowAt = (verdict: ChainVerdict, target: string): number => verdict.rows.findIndex(r => r.roll_type_1 === '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0' && upper(r.roll_to) === upper(target));
  for (const seg of segments) {
    const run = chain.jobs[seg.job]!, step = run.steps[seg.step]!, verdict = run.verdicts[seg.step]!;
    const from = seg.index === 0 ? 0 : seg.cuts[seg.index - 1]!;
    const to = seg.index < seg.cuts.length ? seg.cuts[seg.index]! : verdict.rows.length;
    // a write lands in its damage row's segment; one with no damage row (never produced — the commit gate binds each hit to one row)
    // lands in the last segment rather than being dropped (Codex S4 review #9)
    const inSeg = (i: number): boolean => i < 0 ? to === verdict.rows.length : i >= from && i < to;
    const last = seg.index === seg.cuts.length;
    const creatures: Record<string, Record<string, unknown>> = {};
    // v_current / v_temp as integers (a hydrated row can carry numeric strings; the save's bounds are int-only)
    const int = (v: unknown): unknown => typeof v === 'string' && /^-?\d+$/.test(v) ? Number(v) : v;
    for (const [id, cols] of Object.entries(verdict.ec)) if (inSeg(damageRowAt(verdict, id)))
      creatures[upper(id)] = Object.fromEntries(Object.entries(cols).map(([k, v]) => [k, int(v)]));
    ops.push({ op: 'step', source_inventory_id: run.job.source_inventory_id, ability_id: upper(step.ability_id),
      caster: { kind: run.job.attributed_caster.kind, id: upper(run.job.attributed_caster.id) }, roll_id: upper(run.job.roll_id),
      rows: verdict.rows.slice(from, to),
      statuses: last ? verdict.statusesAdded.map(s => ({ ...s, status_id: upper(s.status_id) || uuid() })) : [],
      objects: verdict.objectDamage.filter(o => inSeg(damageRowAt(verdict, o.inventory_id)))
        .map(o => ({ inventory_id: upper(o.inventory_id), v_current: o.v_current, v_temp: o.v_temp, destroyed: o.destroyed })),
      creatures });
    for (const caused of seg.after) ops.push({ op: 'destroy', command: caused.command });
  }
  return ops;
}

const statusList = (row: Row, mode: 'target' | 'caster'): Row[] => {
  const statuses = (record(row.statuses) ?? (row.statuses = {})) as Row;
  if (!Array.isArray(statuses[mode])) statuses[mode] = [];
  return statuses[mode] as Row[];
};
const entityId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id ?? record(row.inventory)?.inventory_id ?? row.inventory_id);

/** Apply a chain's ops to one encounter copy IN PLACE (idempotent: a row already removed / a status id already held is left alone). */
export function applyChainOps(encounter: Row, ops: readonly ChainOp[], catalog: (id: string) => Row | null): void {
  const encounterId = upper(record(encounter.encounters)?.encounter_id ?? encounter.encounter_id);
  const members = (): Row[] => (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : []).map(record).filter((r): r is Row => !!r);
  const inventories = (): Row[] => (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr as unknown[] : []).map(record).filter((r): r is Row => !!r);
  const find = (id: string): Row | null => members().find(r => entityId(r) === upper(id)) ?? inventories().find(r => entityId(r) === upper(id)) ?? null;
  const cat = (record(encounter.catalog) ?? (encounter.catalog = {})) as Row;
  for (const op of ops) {
    if (op.op === 'destroy') { applyObjectDestruction(encounter, op.command.frame); continue; }
    for (const s of op.statuses) {
      const sid = upper(s.status_id);
      const itemId = upper(s.status_item_id);
      if (itemId && !record(cat[itemId])) { const def = catalog(itemId); if (def) cat[itemId] = def; }
      const target = upper(s.target);
      const entry: Row = { ...s, status_id: sid, status_target_id: target, status_caster_id: upper(s.caster), status_private: 0 };
      delete entry.target; delete entry.caster;
      const holder = target === encounterId ? encounter : find(target);
      if (holder) { const list = statusList(holder, 'target'); if (!list.some(e => upper(e.status_id) === sid)) list.unshift(entry); }
      const caster = upper(s.caster) !== target ? find(upper(s.caster)) : null;
      if (caster) { const list = statusList(caster, 'caster'); if (!list.some(e => upper(e.status_id) === sid)) list.unshift(entry); }
    }
    for (const o of op.objects) {
      const row = inventories().find(r => entityId(r) === upper(o.inventory_id));
      if (!row) continue; // destroyed later in this chain (its frame removed it)
      const computed = (record(row.computed) ?? (row.computed = {})) as Row;
      const vitality = (record(computed.vitality) ?? {}) as Row;
      // the lean override a damaged row carries (the catalog default keeps the max): current AND temp stated explicitly (a 0 temp included,
      // so neither a template's nor a stale hydrated value shows through), every derived key dropped so readers re-derive
      const next: Row = { ...vitality, base_v_current: o.v_current, base_v_temp: o.v_temp };
      for (const derived of ['v_current', 'v_temp', 'v_status', 'v_percent', 'v_bar_v_percent', 'v_bar_total', 'v_lost']) delete next[derived];
      computed.vitality = next;
    }
    for (const [id, cols] of Object.entries(op.creatures)) {
      const member = members().find(r => entityId(r) === upper(id));
      const ec = record(member?.encounter_creatures);
      if (ec) for (const [col, value] of Object.entries(cols)) ec[col] = value;
    }
  }
}

/** Retain only changed slots and removed row references. No encounter/entity snapshot or recovery fetch. */
export function applyChainOpsWithCorrection(encounters: readonly Row[], ops: readonly ChainOp[], catalog: (id: string) => Row | null,
  redraw: (report: UndoReport) => void): Pick<OptimisticDraw, 'accept' | 'refuse'> {
  const corrections: Array<(report: UndoReport) => void> = [];
  const slots = new Map<Row, Set<string>>();
  const capture = (holder: Row, path: string[]): void => {
    const key = path.join('.'), keys = slots.get(holder) ?? new Set<string>();
    if (keys.has(key)) return;
    keys.add(key); slots.set(holder, keys);
    const read = (): { present: boolean; value: unknown } => {
      let at: Row | null = holder;
      for (const part of path.slice(0, -1)) at = record(at?.[part]);
      return { present: !!at && Object.prototype.hasOwnProperty.call(at, path[path.length - 1]!), value: at?.[path[path.length - 1]!] };
    };
    const before = read();
    // Capture after the complete native chain, since a slot may be written by several segments.
    finishes.push(() => {
      const after = read();
      if (before.present === after.present && before.value === after.value) return;
      corrections.push(report => {
        const now = read();
        if (now.present !== after.present || now.value !== after.value) { report.conflicts++; report.conflictPaths.push(key); return; }
        let at = holder;
        for (const part of path.slice(0, -1)) at = record(at[part]) ?? (at[part] = {}) as Row;
        if (before.present) at[path[path.length - 1]!] = before.value; else delete at[path[path.length - 1]!];
        report.reverted++;
      });
    });
  };
  const finishes: Array<() => void> = [];
  const captureRows = (rows: Row[], ids: Set<string>, identify: (row: Row) => string): void => {
    const before = new Map(rows.flatMap((row, index) => ids.has(identify(row)) ? [[identify(row), { row, index }] as const] : []));
    finishes.push(() => {
      const after = new Map(rows.filter(row => ids.has(identify(row))).map(row => [identify(row), row]));
      for (const id of ids) {
        const was = before.get(id), written = after.get(id);
        if (was?.row === written) continue;
        corrections.push(report => {
          const index = rows.findIndex(row => identify(row) === id), now = rows[index];
          if (now !== written) { report.conflicts++; report.conflictPaths.push(id); return; }
          if (index >= 0) rows.splice(index, 1);
          if (was) rows.splice(Math.min(was.index, rows.length), 0, was.row);
          report.reverted++;
        });
      }
    });
  };
  for (const encounter of encounters) {
    const inventory = (Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : []) as Row[];
    const members = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) as Row[];
    const inventoryIds = new Set<string>(), creatureIds = new Set<string>();
    const find = (id: string): Row | undefined => inventory.find(row => entityId(row) === upper(id)) ?? members.find(row => entityId(row) === upper(id));
    for (const op of ops) {
      if (op.op === 'destroy') {
        const frame = op.command.frame;
        for (const id of [...frame.remove, ...frame.add.map(row => inventoryField(row, 'inventory_id')),
          ...(frame.released ?? []).map(row => inventoryField(row, 'inventory_id'))]) inventoryIds.add(upper(id));
        for (const id of frame.creaturesRemoved ?? []) creatureIds.add(upper(id));
        for (const v of frame.vitality ?? []) {
          const row = find(v.inventory_id);
          if (row) for (const path of [['inventory', 'v_current'], ['computed', 'vitality', 'v_current'], ['computed', 'vitality', 'base_v_current']]) capture(row, path);
        }
        for (const retarget of frame.retarget ?? []) {
          const row = find(retarget.inventory_id);
          for (const link of (Array.isArray(row?.inventory_encounter_link) ? row.inventory_encounter_link : []) as Row[])
            if (upper(link.encounter_link_id) === upper(retarget.encounter_link_id)) capture(link, ['encounter_link_to']);
        }
        continue;
      }
      for (const o of op.objects) { const row = find(o.inventory_id); if (row) capture(row, ['computed', 'vitality']); }
      for (const [id, cols] of Object.entries(op.creatures)) {
        const row = find(id); if (row) for (const key of Object.keys(cols)) capture(row, ['encounter_creatures', key]);
      }
      for (const s of op.statuses) for (const [id, mode] of [[s.target, 'target'], [s.caster, 'caster']] as const) {
        const holder = upper(id) === upper(record(encounter.encounters)?.encounter_id) ? encounter : find(upper(id));
        if (holder) {
          capture(holder, ['statuses']);
          capture(holder, ['statuses', mode]);
          const list = statusList(holder, mode);
          captureRows(list, new Set([upper(s.status_id)]), row => upper(row.status_id));
        }
      }
    }
    captureRows(inventory, inventoryIds, entityId);
    captureRows(members, creatureIds, entityId);
  }
  for (const encounter of encounters) applyChainOps(encounter, ops, catalog);
  for (const finish of finishes) finish();
  let settled = false;
  return {
    accept: () => { settled = true; corrections.length = 0; },
    refuse: () => {
      if (settled) return null;
      settled = true;
      const report: UndoReport = { reverted: 0, kept: 0, conflicts: 0, conflictPaths: [] };
      for (const correct of corrections.reverse()) correct(report);
      corrections.length = 0; redraw(report); return report;
    },
  };
}
